import React, { ReactNode } from 'react';
import {Box, InputLabel, Progress} from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { Control, UseFormReturn } from 'react-hook-form';

import ControlledAutocomplete from './ControlledAutocomplete';
import { IExamMasterCodeItem } from '../../api/user/model';
import NumberOnlyFormItem from './NumberOnlyFormItem';
import { useGetIExamMasterCode } from '../../api-refactored/user/useGetIExamMasterCode';

interface InputAttributes {
  /**
   * Label of the form item.
   */
  label?: string;
  /**
   * Name of the form item.
   */
  name?: string;
  /**
   * Indication whether the form item should have error styling.
   */
  error?: boolean;
  /**
   * Text displayed beneath the form item.
   */
  helperText?: string;
  /**
   * Set to `true` to disable the form item.
   */
  disabled?: boolean;
}

interface ContactNumberFormItemProps {
  id?: string;
  containerClassName?: string;
  /**
   * Hook object returned from react-hook-form useForm() method
   */
  formHook: UseFormReturn<any>;
  countryCodeControl?: Control;
  countryCodeRules?: Object;
  numberRegister?: any;
  /**
   * The main label shown on top
   */
  label?: ReactNode;
  /**
   * Props that will be passed to countryCode Autocomplete component
   */
  countryCode?: InputAttributes;
  /**
   * Props that will be passed to phone number TextField component
   */
  number?: InputAttributes;

  countryCodeDefaultValue?: IExamMasterCodeItem | string;
  countryCodeisOptionEqualToValue?: (
    option: IExamMasterCodeItem,
    value: IExamMasterCodeItem
  ) => boolean;

  numberDefaultValue?: any;

  isSkipTransform?: boolean;
  placeholder?: string;
  onCountryChange?: (value: string) => void;
  isPasteDisabled?: boolean;
  isRequired?: boolean;
  isDefaultToSingapore?: boolean;
  isIExamsApiEnabled?: boolean;
}

const useStyles = makeStyles()((theme) =>
({
  inputsContainer: {
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  singleRowLeftItemContainer: {
    flex: 1,
    minWidth: 205,
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  contactNumberContainer: {
    flex: 3,
  },
  requiredLabel: {
    color: theme.color.state.stateImportant,
    marginLeft: theme.spacing(0.5),
    display: 'inline',
  },
  disabledLabel: {
    color: theme.color.text.textDisabled,
  },
}));

/**
 * This is a convenient wrapper for contact number form item that consists of a country code dropdown and a phone/mobile
 * number text input. Country code dropdown values are retrieved from IExam Master code API (/exam-details).
 *
 * This component is meant to be used with react-hook-form. The country code selected value will be the `code` of each
 * country code dropdown item. For example, the countryCode dropdown list is an array [{ code: '51251', description: 'Singapore (+65)' }, ...],
 * when singapore is selected, the value will be '51251'.
 */
export const ContactNumberFormItem = ({
  id,
  containerClassName = undefined,
  formHook,
  countryCodeControl = undefined,
  countryCodeRules = undefined,
  numberRegister = undefined,
  label = undefined,
  countryCode = {},
  number = {},
  countryCodeDefaultValue = undefined,
  countryCodeisOptionEqualToValue = undefined,
  numberDefaultValue = undefined,
  isSkipTransform = undefined,
  placeholder = 'Enter Mobile No.',
  onCountryChange,
  isPasteDisabled = false,
  isRequired = false,
  isDefaultToSingapore = false,
  isIExamsApiEnabled = true,
}: ContactNumberFormItemProps) => {
  const { classes } = useStyles();
  const { data, isLoading, error, refetch } = useGetIExamMasterCode({
    enabled: isIExamsApiEnabled,
  });
  const singaporeDefaultCountryCode = isDefaultToSingapore
    ? data?.countryCode.find((code) => code.codeValue === '65')
    : undefined;
  const { control } = formHook;

  const {
    label: countryCodeLabel = undefined,
    name: countryCodeName = 'countryCode',
    error: countryCodeError,
    helperText: countryCodeHelperText,
    disabled: countryCodeDisabled,
  } = countryCode;

  const {
    label: numberLabel = undefined,
    name: numberName = 'number',
    error: numberError,
    helperText: numberHelperText,
    disabled: numberDisabled,
  } = number;
  const isDisabled = numberDisabled && countryCodeDisabled;
  if(isLoading){
    return <Progress color="secondary" />
  }
  return (
    <Box className={containerClassName}>
      {label && (
        <InputLabel disabled={isDisabled} error={countryCodeError && numberError}>
          <>
            {label}
            {isRequired && (
              <div className={`${classes.requiredLabel} ${isDisabled && classes.disabledLabel}`}>
                *
              </div>
            )}
          </>
        </InputLabel>
      )}

      <Box className={classes.inputsContainer}>
        <ControlledAutocomplete
          id={`inputSelect-${id}-contactCountry`}
          className={classes.singleRowLeftItemContainer}
          control={countryCodeControl ?? control}
          rules={countryCodeRules}
          label={countryCodeLabel}
          name={countryCodeName}
          disabled={countryCodeDisabled}
          error={countryCodeError}
          helperText={countryCodeHelperText}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.codeValueDescription)}
          renderOption={(_, option)=>option.codeValueDescription}
          loading={isLoading}
          loadingError={!!error}
          loadingErrorOnReFetch={refetch}
          // @ts-ignore
          options={data?.countryCode || []}
          transform={
            isSkipTransform
              ? undefined
              : {
                input: (selectedOption, options) => {
                  const selected = options.find(({ codeValue }) => codeValue === selectedOption);
                  return selected ?? null;
                },
                output: (selectedOption) => (selectedOption ? selectedOption.codeValue : null),
              }
          }
          defaultValue={
            isDefaultToSingapore ? singaporeDefaultCountryCode : countryCodeDefaultValue
          }
          isOptionEqualToValue={countryCodeisOptionEqualToValue}
          placeholder="Select country code"
          onInputChange={(_evt, value) => {
            if (onCountryChange) {
              onCountryChange(value);
            }
          }}
        />
        <NumberOnlyFormItem
          id={`inputText-${id}-contactNumber`}
          className={classes.contactNumberContainer}
          label={numberLabel}
          name={numberName}
          type="tel"
          form={formHook}
          disabled={numberDisabled}
          error={numberError}
          helperText={numberHelperText}
          fullWidth
          variant="outlined"
          defaultValue={numberDefaultValue}
          placeholder={placeholder}
          isPasteDisabled={isPasteDisabled}
        />
      </Box>
    </Box>
  );
};
